// src/pages/AdminsPage.js
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { admins as adminsApi } from '../utils/api';
import useAuthStore from '../store/authStore';
import Pagination, { usePagination } from '../components/Pagination';

const ROLE_BADGE = { superadmin: 'badge-danger', admin: 'badge-primary', viewer: 'badge-muted' };

export default function AdminsPage() {
  const qc   = useQueryClient();
  const me   = useAuthStore(s => s.admin);
  const [form, setForm] = useState({ username: '', email: '', password: '', full_name: '', role: 'admin' });
  const [showForm, setShowForm] = useState(false);

  const { data: list, isLoading } = useQuery({
    queryKey: ['admins'],
    queryFn: () => adminsApi.list().then(r => r.data.data)
  });

  const createMut = useMutation({
    mutationFn: () => adminsApi.create(form),
    onSuccess: () => { toast.success('Admin created'); qc.invalidateQueries(['admins']); setShowForm(false); setForm({ username:'', email:'', password:'', full_name:'', role:'admin' }); },
    onError: err => toast.error(err.response?.data?.message || 'Failed')
  });

  const toggleMut = useMutation({
    mutationFn: id => adminsApi.toggle(id),
    onSuccess: () => { toast.success('Status toggled'); qc.invalidateQueries(['admins']); }
  });

  const deleteMut = useMutation({
    mutationFn: id => adminsApi.delete(id),
    onSuccess: () => { toast.success('Admin deleted'); qc.invalidateQueries(['admins']); }
  });

  const { paginated: admins, page, setPage, totalPages } = usePagination(list || [], 10);

  if (me?.role !== 'superadmin') {
    return <div className="empty-state" style={{ paddingTop: 80 }}><div className="icon">🔑</div>Superadmin access required.</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">🔑 Admin Users</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(s => !s)}>
          {showForm ? '✕ Cancel' : '+ Add Admin'}
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><span className="card-title">New Admin User</span></div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Username</label>
              <input className="form-control" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} placeholder="jdoe" />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-control" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jdoe@example.com" />
            </div>
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input className="form-control" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="John Doe" />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input className="form-control" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Min 8 chars" />
            </div>
            <div className="form-group">
              <label className="form-label">Role</label>
              <select className="form-control" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                <option value="viewer">Viewer (read-only)</option>
                <option value="admin">Admin (full access)</option>
                <option value="superadmin">Superadmin</option>
              </select>
            </div>
          </div>
          <div className="alert alert-info" style={{ marginBottom: 12 }}>
            Password must be at least 8 characters with one uppercase letter and one number.
          </div>
          <button className="btn btn-primary" onClick={() => createMut.mutate()} disabled={createMut.isPending}>
            {createMut.isPending ? 'Creating…' : '+ Create Admin'}
          </button>
        </div>
      )}

      <div className="card">
        {isLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><span className="spinner" /></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Username</th><th>Full Name</th><th>Email</th><th>Role</th><th>Status</th><th>Last Login</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {admins.map(a => (
                  <tr key={a.id}>
                    <td><strong>{a.username}</strong>{a.id === me?.id && <span className="badge badge-info" style={{ marginLeft: 8 }}>You</span>}</td>
                    <td>{a.full_name || '—'}</td>
                    <td>{a.email}</td>
                    <td><span className={`badge ${ROLE_BADGE[a.role]}`}>{a.role}</span></td>
                    <td><span className={`badge ${a.is_active ? 'badge-success' : 'badge-muted'}`}>{a.is_active ? 'Active' : 'Disabled'}</span></td>
                    <td className="text-mono">{a.last_login ? new Date(a.last_login).toLocaleDateString() : '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {a.id !== me?.id && (
                          <>
                            <button className="btn btn-secondary btn-sm"
                              onClick={() => { if (window.confirm(`${a.is_active ? 'Disable' : 'Enable'} admin account "${a.username}"?`)) toggleMut.mutate(a.id); }}>
                              {a.is_active ? 'Disable' : 'Enable'}
                            </button>
                            <button className="btn btn-danger btn-sm"
                              onClick={() => { if (window.confirm(`Delete admin "${a.username}"?`)) deleteMut.mutate(a.id); }}>
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        )}
      </div>
    </div>
  );
}
